import { alphaSort } from 'vet-bones/utils';

import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectRulesForTreeSelector } from 'src/selectors/projectRulesForTree';
import {
  projectRulesWithAttributeSelector,
  projectRulesWithClassAttributeSelector,
} from 'src/selectors/projectRulesWith';
import { EntityDrawerType } from 'src/store/ui/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const useRulesForAttributeRow = (
  projectId: string,
  attributeIri: string,
  classIri: string
) => {
  const rulesForAttribute = useReduxSelector(
    projectRulesWithAttributeSelector(projectId, attributeIri)
  );
  const rulesForClassAttribute = useReduxSelector(
    projectRulesWithClassAttributeSelector(projectId, classIri, attributeIri)
  );

  return classIri ? rulesForAttribute : rulesForClassAttribute;
};

// if no class (opened from SPG drawer), then we select the first class
// (sorted by how it shows on Rule Overview drawer sidebar)
const useFirstClassValue = (projectId: string, attributeIri: string) => {
  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));
  const ruleInfoByClass = useReduxSelector(
    projectRulesForTreeSelector(projectId)
  );

  let classValue = '';
  Object.entries(ruleInfoByClass)
    .sort(alphaSort(([classIri]) => getLabelForIri(labelsByIri, classIri)))
    .some(([classIri, { attributes }]) => {
      if (attributes.has(attributeIri)) {
        classValue = classIri;
        return true;
      }
      return false;
    });

  return classValue;
};

export const useOpenRulesForAttribute = (
  attributeIri: string
):
  | { openRules?: never; openCreateRules: () => void }
  | { openRules: () => void; openCreateRules?: never } => {
  const dispatch = useReduxDispatch();

  const { projectId } = useRouterParams();
  const entityDrawer = useReduxSelector(entityDrawerSelector);
  const firstClassValue = useFirstClassValue(projectId, attributeIri);
  const classIri =
    entityDrawer.type === EntityDrawerType.CLASS
      ? (entityDrawer.entityValue as string)
      : firstClassValue;
  const rulesForAttribute = useRulesForAttributeRow(
    projectId,
    attributeIri,
    classIri
  );
  const doesAttributeHaveRules = Boolean(Object.keys(rulesForAttribute).length);

  return doesAttributeHaveRules
    ? {
        openRules: () =>
          dispatch(
            uiSlice.actions.toggledRuleOverviewDrawer({
              isOpen: true,
            })
          ),
      }
    : {
        openCreateRules: () =>
          dispatch(uiSlice.actions.toggledRuleDetailDrawer({ isOpen: true })),
      };
};
